import FullHeightView from 'components/new/FullHeightView';
import { useLayoutContext } from 'contexts/LayoutContext';
import { ActionStatus } from 'further-types/lp/action';
import { InvestmentRevaluationOption } from 'further-types/lp/investment';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { SortOrder } from 'further-types/utils';
import { investment as rules } from 'further-ui/validations';
import api from 'lib/trpcClient';
import { round, isEqual } from 'lodash';
import { useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import RevaluationFormWithFxRate from 'routes/LpPages/Company/Revaluations/CreateRevaluation/RevaluationFormWithFxRate';
import {
  applyRevaluationOption,
  buildBaselineRevaluations,
} from './deriveRevaluations';
import RevaluationOptions from './RevaluationOptions';
import RevaluationStepActions from './RevaluationStepActions';

type RevalueInvestmentsProps = {
  onSuccess: () => void;
  onCancel: () => void;
};

const RevalueInvestments: React.FC<RevalueInvestmentsProps> = ({
  onSuccess,
  onCancel,
}) => {
  const { vehicle, selectedVehicleId } = useLayoutContext();

  const { watch, setValue, control, getValues } =
    useFormContext<rules.CreateInvestmentFormValues>();

  const { replace: replaceNewRevaluations } = useFieldArray({
    control,
    name: 'newRevaluations',
  });

  const status = watch('status');
  const assetId = watch('assetId');
  const revaluationOption = watch('revaluationOption');
  const holdingClasses = watch('holdingClasses');
  const vehicleId = selectedVehicleId ?? '';

  const [asset] = api.asset.get.useSuspenseQuery({ assetId, vehicleId });

  const [holdingClassInvestmentTotals] =
    api.asset.getAssetHoldingClassInvestmentTotals.useSuspenseQuery({
      assetId,
      vehicleId,
    });

  const [revaluations] = api.asset.getAssetRevaluations.useSuspenseQuery({
    assetId,
    vehicleId,
  });

  const [assetFullHistory] = api.asset.getAssetFullHistory.useSuspenseQuery({
    assetId,
    vehicleId,
  });

  const [investmentHistory] = api.asset.getInvestmentHistory.useSuspenseQuery({
    assetId,
    vehicleId,
    order: SortOrder.Desc,
    orderBy: 'dealCompletionDate',
  });

  const assetBaseCurrency = asset.baseCurrency ?? CurrencyCode.GBP;

  const baseline = buildBaselineRevaluations({
    holdingClassInvestmentTotals,
    assetFullHistory,
    assetId,
    assetBaseCurrency,
  });

  useEffect(() => {
    const expectedRevaluations = revaluations.map((rev) => ({
      ...rev,
      isNew: false as const,
    }));

    const currentAssetCurrency = getValues('assetCurrency');
    const currentInvestmentHistory = getValues('investmentHistory') ?? [];
    const currentRevaluations = getValues('revaluations') ?? [];

    const isApiDataSeeded =
      currentAssetCurrency === assetBaseCurrency &&
      isEqual(currentInvestmentHistory, investmentHistory) &&
      isEqual(currentRevaluations, expectedRevaluations);

    if (isApiDataSeeded) {
      return;
    }

    setValue('assetCurrency', assetBaseCurrency);
    setValue('investmentHistory', investmentHistory);
    setValue('revaluations', expectedRevaluations);

    const applied = applyRevaluationOption(
      revaluationOption,
      baseline,
      holdingClasses,
      investmentHistory,
    );
    replaceNewRevaluations(applied.newRevaluations);
    setValue('currentUnitPrice', applied.currentUnitPrice);
    setValue('currentHoldValue', applied.currentHoldValue);
  }, [
    getValues,
    setValue,
    replaceNewRevaluations,
    assetBaseCurrency,
    investmentHistory,
    revaluations,
    revaluationOption,
    holdingClasses,
    baseline,
  ]);

  if (!vehicle) {
    return null;
  }

  const onSaveDraft = status === ActionStatus.Approved ? undefined : onSuccess;
  const isViewOnly =
    revaluationOption !== InvestmentRevaluationOption.ManuallyInput;

  // On continue: drop revaluations that match the existing unit price (no real
  // change) and fan the global FX rate out across the holding classes, then
  // advance to the approval step.
  const onContinue = () => {
    const currentNewRevaluations = watch('newRevaluations') ?? [];
    const changedRevaluations = currentNewRevaluations.filter((rev) => {
      const existingHoldingClass = holdingClassInvestmentTotals.find(
        (hc) => hc.holdingClassId === rev.holdingClassId,
      );
      return (
        round(existingHoldingClass?.latestUnitPrice ?? 0, 10) !==
        round(rev.unitPriceTcy, 10)
      );
    });
    replaceNewRevaluations(changedRevaluations);

    const currencyRates = watch('revaluationCurrencyRates');
    const globalRate = currencyRates?.global;
    if (globalRate) {
      setValue('revaluationCurrencyRates', {
        ...currencyRates,
        ...Object.fromEntries(
          holdingClassInvestmentTotals.map((hc) => [
            hc.holdingClassId,
            currencyRates?.[hc.holdingClassId] ?? globalRate,
          ]),
        ),
      });
    }

    onSuccess();
  };

  const onContinueWithoutRevaluation = () => {
    replaceNewRevaluations([]);
    setValue('revaluations', []);
    setValue('investmentHistory', []);
    setValue('revaluationCurrencyRates', undefined);
    onSuccess();
  };

  const revaluationOptions = (
    <RevaluationOptions
      holdingClassInvestmentTotals={holdingClassInvestmentTotals}
      baseline={baseline}
      investmentHistory={investmentHistory}
    />
  );

  if (revaluationOption === InvestmentRevaluationOption.DoNotRevalue) {
    return (
      <FullHeightView
        actionBar={
          <RevaluationStepActions
            onPreviousStep={onCancel}
            onSaveDraft={onSaveDraft}
            onContinue={onContinueWithoutRevaluation}
          />
        }
      >
        {revaluationOptions}
      </FullHeightView>
    );
  }

  return (
    <>
      {revaluationOptions}
      <RevaluationFormWithFxRate
        assetName={asset.name}
        assetCurrency={assetBaseCurrency}
        assetId={asset.id}
        vehicleId={vehicleId}
        vehicle={vehicle}
        holdingClassInvestmentTotals={holdingClassInvestmentTotals}
        assetFullHistory={assetFullHistory}
        viewOnly={isViewOnly}
        onSuccess={onContinue}
        renderButtons={({ onSubmit, onError, handleSubmit }) => (
          <RevaluationStepActions
            onPreviousStep={onCancel}
            onSaveDraft={onSaveDraft}
            onContinue={handleSubmit(onSubmit, onError)}
          />
        )}
      />
    </>
  );
};

export default RevalueInvestments;
